/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Andrew Merrill <andrew.merrill@deliciousai.com>
 */

import { ApolloClient, ApolloLink, HttpLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { addOperationNameToUrl } from '@dai/common'
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist'

import cache from './cache'
import { ClientCredentialToken, getAccessToken } from './ClientCredentialToken'
import { auth } from './Firebase'

const {
  VITE_APP__GNARNIA_API_URL,
  VITE_APP__SABRE_API_URL,
  VITE_APP__ML_ULTRA_API_URL,
  VITE_APP__SABRE_CLIENT_ID,
  VITE_APP__SABRE_CLIENT_SECRET,
  DEV,
} = import.meta.env

let sabreToken: ClientCredentialToken | null = null

const getSabreToken = async (): Promise<ClientCredentialToken | null> => {
  const tokenUrl = `${new URL(VITE_APP__SABRE_API_URL).origin}/o/token/`

  if (sabreToken && !sabreToken?.needsRefresh()) {
    return sabreToken
  }

  const token = await getAccessToken(
    tokenUrl,
    VITE_APP__SABRE_CLIENT_ID,
    VITE_APP__SABRE_CLIENT_SECRET,
  )

  sabreToken = token
  return token
}

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations,
        )}, Path: ${path}`,
      )
    })
  if (networkError) console.log(`[Network error]: ${networkError}`)
})

const getServiceUrl = (endpoint: string) => {
  switch (endpoint) {
    case 'sabre':
      return VITE_APP__SABRE_API_URL
    case 'mlUltra':
      return VITE_APP__ML_ULTRA_API_URL
    default:
      return VITE_APP__GNARNIA_API_URL
  }
}

const uriLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext()
  const serviceUrl = getServiceUrl(context.endPoint)
  operation.setContext({
    ...context,
    uri: `${new URL(serviceUrl).origin}/graphql/`,
  })
  return forward(operation)
})

const authLink = setContext(async (_, context) => {
  try {
    const token =
      context.endPoint === 'sabre'
        ? (await getSabreToken())?.accessToken
        : await auth.currentUser?.getIdToken()

    return {
      ...context,
      headers: {
        ...context.headers,
        ...(token && { authorization: `Bearer ${token}` }),
        TIMEZONE: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }
  } catch (error) {
    return context
  }
})

export const client = new ApolloClient({
  link: ApolloLink.from([
    uriLink,
    addOperationNameToUrl,
    ...(DEV ? [errorLink] : []),
    authLink,
    new HttpLink({ credentials: 'same-origin' }),
  ]),
  cache,
})

export const persistLocalCache = async (): Promise<void> => {
  await persistCache({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
  })
}
